import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useProfile } from './useProfile';

export interface TourStep {
  id: number;
  targetSelector: string;
  title: string;
  message: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action: string;
}

/** Product tour: 9 steps — user must complete each action to advance */
export const TOUR_STEPS: TourStep[] = [
  {
    id: 1,
    targetSelector: '[data-tour-target="add-task-form"]',
    title: 'Add a task',
    message: "Type something you want to do today and press Enter or tap Add Task.",
    position: 'bottom',
    action: 'add-task',
  },
  {
    id: 2,
    targetSelector: '[data-tour-target="main-task-zone"]',
    title: 'Set your ONE Thing',
    message: "Drag your task into 'The ONE Thing' zone, or use the ⋮ menu and tap 'Set as ONE Thing'.",
    position: 'bottom',
    action: 'set-main-task',
  },
  {
    id: 3,
    targetSelector: '[data-tour-target="start-task-button"]',
    title: 'Start a focus session',
    message: 'Tap the play button on your ONE Thing task to start working.',
    position: 'left',
    action: 'start-task',
  },
  {
    id: 4,
    targetSelector: '[data-tour-target="timer-mode-select"]',
    title: 'Pick your mode',
    message: 'Choose Deep Flow (open-ended) or Manager Mode (timed). Pick one to start.',
    position: 'bottom',
    action: 'select-mode',
  },
  {
    id: 5,
    targetSelector: '[data-tour-target="stop-timer-button"]',
    title: 'Stop when done',
    message: "When you're done, tap Stop on the timer bar to end the session.",
    position: 'top',
    action: 'stop-task',
  },
  {
    id: 6,
    targetSelector: '[data-tour-target="task-checkbox"]',
    title: 'Complete a task',
    message: "Tap the checkbox to mark your task as done. Celebrate your wins!",
    position: 'right',
    action: 'complete-task',
  },
  {
    id: 7,
    targetSelector: '[data-tour-target="move-to-backlog-trigger"]',
    title: 'Save for later',
    message: "Tap the ⋮ menu, then 'Move to All Todos' to save a task for another day.",
    position: 'left',
    action: 'move-to-backlog',
  },
  {
    id: 8,
    targetSelector: '[data-tour-target="nav-tabs"]',
    title: 'Explore your views',
    message: 'Use these tabs to see your Week plan or History with stats and AI summaries.',
    position: 'top',
    action: 'change-view',
  },
  {
    id: 9,
    targetSelector: '', // No target — centered celebration
    title: "You're all set! 🎉",
    message: "You're ready to focus! Add tasks, work on your ONE Thing, and track your progress in History.",
    position: 'center',
    action: 'auto-complete',
  },
];

interface TourContextValue {
  currentStep: number;
  isActive: boolean;
  steps: TourStep[];
  advanceStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  startTour: () => void;
  getCurrentStepData: () => TourStep | null;
  notifyAction: (action: string) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { profile, updateProfile, loading } = useProfile();
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize tour state from profile
  useEffect(() => {
    if (!loading && profile && !initialized) {
      const tourStep = profile.tour_step ?? 0;
      
      // If tour_step is between 1 and max steps, resume tour (e.g. they left mid-tour)
      if (tourStep >= 1 && tourStep <= TOUR_STEPS.length) {
        setCurrentStep(tourStep);
        setIsActive(true);
      }
      // Do NOT auto-start here: guided tour only starts on first load after signup (see Index.tsx + location.state.firstVisitAfterOnboarding)
      setInitialized(true);
    }
  }, [loading, profile, initialized, updateProfile]);

  const advanceStep = useCallback(async () => {
    const nextStep = currentStep + 1;
    
    if (nextStep > TOUR_STEPS.length) {
      // Tour complete
      setIsActive(false);
      setCurrentStep(0);
      await updateProfile({ tour_step: 0, tour_completed: true });
    } else {
      setCurrentStep(nextStep);
      await updateProfile({ tour_step: nextStep });
    }
  }, [currentStep, updateProfile]);

  const skipTour = useCallback(async () => {
    setIsActive(false);
    setCurrentStep(0);
    await updateProfile({ tour_step: 0, tour_completed: true });
  }, [updateProfile]);

  const completeTour = useCallback(async () => {
    setIsActive(false);
    setCurrentStep(0);
    await updateProfile({ tour_step: 0, tour_completed: true });
  }, [updateProfile]);

  const startTour = useCallback(async () => {
    setCurrentStep(1);
    setIsActive(true);
    await updateProfile({ tour_step: 1 });
  }, [updateProfile]);

  const getCurrentStepData = useCallback(() => {
    if (!isActive || currentStep === 0) return null;
    return TOUR_STEPS.find(s => s.id === currentStep) || null;
  }, [currentStep, isActive]);

  // Notify when an action is completed
  const notifyAction = useCallback((action: string) => {
    if (!isActive) return;
    
    const currentStepData = TOUR_STEPS.find(s => s.id === currentStep);
    if (currentStepData && currentStepData.action === action) {
      // Small delay for visual feedback
      setTimeout(() => {
        advanceStep();
      }, 500);
    }
  }, [isActive, currentStep, advanceStep]);

  return (
    <TourContext.Provider
      value={{
        currentStep,
        isActive,
        steps: TOUR_STEPS,
        advanceStep,
        skipTour,
        completeTour,
        startTour,
        getCurrentStepData,
        notifyAction,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useProductTour() {
  const context = useContext(TourContext);
  if (!context) {
    // Return a safe no-op context when used outside provider (during loading/redirects)
    return {
      currentStep: 0,
      isActive: false,
      steps: TOUR_STEPS,
      advanceStep: () => {},
      skipTour: () => {},
      completeTour: () => {},
      startTour: () => {},
      getCurrentStepData: () => null,
      notifyAction: () => {},
    };
  }
  return context;
}
